import { create } from 'zustand';
import type { Group, Mesh, Object3D } from 'three/webgpu';
import type { PanelFader } from '../store/panelTypes';

export interface InstrumentRefs {
  root: Group | null;
  keybed: Object3D | null;
  controls: Object3D | null;
  faders: Object3D | null;
  displays: Object3D | null;
  /** MIDI note number -> the key mesh that plays it. */
  keys: Map<number, Mesh>;
  /** Mesh name -> button mesh, for both highlighting and indicator placement. */
  buttons: Map<string, Mesh>;
  faderCaps: Map<PanelFader, Object3D>;
}

interface InstrumentStore extends InstrumentRefs {
  ready: boolean;
  setRefs: (refs: Omit<InstrumentRefs, never>) => void;
  clear: () => void;
}

const EMPTY: InstrumentRefs = {
  root: null,
  keybed: null,
  controls: null,
  faders: null,
  displays: null,
  keys: new Map(),
  buttons: new Map(),
  faderCaps: new Map(),
};

/**
 * Object references resolved out of the loaded model.
 *
 * The indicator overlay needs to sit on specific buttons, and it renders in a
 * separate component from the one that loads the model, so the lookup tables
 * live here rather than being threaded through props.
 */
export const useInstrumentStore = create<InstrumentStore>((set) => ({
  ...EMPTY,
  ready: false,
  setRefs: (refs) => set({ ...refs, ready: true }),
  clear: () => set({ ...EMPTY, ready: false }),
}));
